import { Spinner } from '../components/feedback';
import { WorldGenerationSection } from './WorldGenerationSection';
import { OperationBoundary } from '../components/OperationBoundary';
// サーバーの削除。ID の入力（取り違え防止）とワンタイムコードの確認（本人確認）を 1 画面で行い、そのまま実行する。
// 内部では他の操作と同じく変更リクエストを作って承認する（監査ログに残る）。途中でやめたらリクエストは却下する。
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Empty } from '../components/ui';

function DangerTabContent({ server }: { server: ServerView }) {
  const { canOperate, ensureSteppedUp, notify } = useApp();
  const navigate = useNavigate();
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    let request: ChangeRequest | undefined;
    try {
      request = await api.createRequest('delete_server', {}, server.id);
      // 取り消せない操作なので、5 分以内に入れたコードがあっても入れ直す
      await ensureSteppedUp({ fresh: true });
      await api.approveRequest(request.id);
      notify(`${server.name} の削除を開始しました`);
      navigate('/');
    } catch (error) {
      if (request) await api.rejectRequest(request.id).catch(() => undefined);
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  if (!canOperate) return <Empty>この操作を行う権限がありません。</Empty>;
  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <section className="card card-body stack" style={{ borderColor: 'var(--danger)' }}>
        <h2 data-search-id="delete-server">サーバーを削除</h2>
        <ul className="small" style={{ margin: 0, paddingLeft: 20 }}>
          <li>VM とデータディスク（ワールド・設定・プラグイン）を削除します。元に戻せません。</li>
          <li>スナップショットも消えます。PBS のバックアップは残りますが、そこからの復旧は手作業になります。</li>
          <li>起動中なら、先に Minecraft を止めてから削除します。</li>
        </ul>
        <div className="field">
          <label htmlFor="confirm-name">確認のためサーバー ID「{server.id}」を入力</label>
          <input
            id="confirm-name"
            className="input"
            autoComplete="off"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
        </div>
        <div className="row">
          <span className="small faint">ボタンを押すとワンタイムコードで本人確認し、そのまま削除します。</span>
          <span className="spacer" />
          <button
            className="btn danger-solid"
            disabled={confirmName !== server.id || busy}
            onClick={() => void remove()}
          >
            {busy && <Spinner />}
            ワンタイムコードで確認して削除
          </button>
        </div>
      </section>
    </div>
  );
}

export function DangerTab(props: Parameters<typeof DangerTabContent>[0]) {
  return (
    <OperationBoundary serverId={props.server.id}>
      <div className="stack" style={{ maxWidth: 760 }}>
        <WorldGenerationSection server={props.server} />
        <DangerTabContent {...props} />
      </div>
    </OperationBoundary>
  );
}
